import { DashboardLayout } from '@/layout/DashboardLayout';
import { Button, Dialog, ManagedDataTable } from '@/design-system';
import { Puzzle, RefreshCw, Shield, FlaskConical } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ModuleDefinition } from './api/modules.service';
import { useModulesRegistryQuery } from './hooks/useModulesRegistryQuery';
import { logger } from '@/core/logger/logger';
import { type ColumnDef } from '@tanstack/react-table';
import { ModulePackagesTable } from './components/ModulePackagesTable';

const TYPE_LABELS: Record<string, { label: string; icon: typeof Shield; className: string }> = {
  core: { label: 'Core', icon: Shield, className: 'bg-blue-100 text-blue-800' },
  addon: { label: 'Add-on', icon: Puzzle, className: 'bg-amber-100 text-amber-800' },
  experimental: { label: 'Experimental', icon: FlaskConical, className: 'bg-purple-100 text-purple-800' },
};
const ENVIRONMENT_LABELS: Record<string, string> = { development: 'Desenvolvimento', dev: 'Desenvolvimento', staging: 'Homologação', production: 'Produção', prod: 'Produção', test: 'Teste' };

export function ModulesPage() {
  const [registryOpen, setRegistryOpen] = useState(false);
  const { data, isLoading, isError, error, refetch } = useModulesRegistryQuery();
  const modules = useMemo(() => data?.modules ?? [], [data?.modules]);
  const environment = data?.environment ?? '';
  const environmentLabel = ENVIRONMENT_LABELS[environment] ?? (environment || 'Carregando…');
  const environmentOptions = useMemo(() => [...new Set(['development', 'staging', 'production', ...modules.flatMap((module) => Array.isArray(module.environments) ? module.environments : [])])].map((value) => ({ value, label: ENVIRONMENT_LABELS[value] ?? value })), [modules]);

  useEffect(() => {
    if (isError && error) {
      logger.error('[modules-page]', error instanceof Error ? error.message : String(error));
    }
  }, [isError, error]);

  const getTypeConfig = (type: string) =>
    TYPE_LABELS[type] ?? { label: type, icon: Puzzle, className: 'bg-gray-100 text-gray-800' };

  const sortedModules = useMemo(
    () => [...modules].sort((a, b) => (a.sidebarOrder ?? 999) - (b.sidebarOrder ?? 999)),
    [modules]
  );

  const columns = useMemo<ColumnDef<ModuleDefinition>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Módulo',
      cell: ({ row }) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{row.original.name}</div>
          <div className="text-xs text-gray-500">{row.original.id}</div>
          {row.original.description && <div className="text-xs text-gray-600 mt-1">{row.original.description}</div>}
        </div>
      ),
    },
    {
      accessorKey: 'type',
      header: 'Tipo',
      minSize: 140,
      cell: ({ row }) => {
        const typeConfig = getTypeConfig(row.original.type);
        const TypeIcon = typeConfig.icon;
        return (
          <span className={`inline-flex items-center gap-1 whitespace-nowrap px-2 py-1 text-xs font-medium rounded-full ${typeConfig.className}`}>
            <TypeIcon size={12} className="shrink-0" />
            {typeConfig.label}
          </span>
        );
      },
    },
    {
      accessorKey: 'featureKey',
      header: 'Feature Key',
      cell: ({ row }) => <span className="text-sm text-gray-700">{row.original.featureKey || '-'}</span>,
    },
    {
      accessorKey: 'path',
      header: 'Path',
      cell: ({ row }) => <code className="text-xs bg-gray-100 px-2 py-1 rounded">{row.original.path ?? '-'}</code>,
    },
    {
      accessorKey: 'environments',
      header: 'Ambientes',
      filterFn: (row, _columnId, value: string) => row.original.environments === 'all' || (Array.isArray(row.original.environments) && row.original.environments.includes(value)),
      cell: ({ row }) => {
        const envLabel = row.original.environments === 'all'
          ? 'Todos'
          : Array.isArray(row.original.environments)
            ? row.original.environments.map((value) => ENVIRONMENT_LABELS[value] ?? value).join(', ')
            : '-';
        return <span className="text-xs text-gray-600">{envLabel}</span>;
      },
    },
    {
      accessorKey: 'sidebarOrder',
      header: 'Ordem',
      cell: ({ row }) => <span className="text-sm text-gray-600">{row.original.sidebarOrder ?? '-'}</span>,
    },
  ], []);

  return (
    <DashboardLayout title="Módulos">
      <ModulePackagesTable onViewModules={() => setRegistryOpen(true)} />
      <Dialog isOpen={registryOpen} onClose={() => setRegistryOpen(false)} title={`Módulos: Ambiente ${environmentLabel}`} icon={<Puzzle />} size="2xl" footer={<Button variant="subtle" onClick={() => setRegistryOpen(false)}>Fechar módulos</Button>}>
        {isError && <p role="alert" className="mb-3 text-sm text-red-600">Não foi possível carregar os módulos. Tente atualizar.</p>}
        <ManagedDataTable
          appearance="embedded"
          title="Lista de Módulos"
          columns={columns}
          data={sortedModules}
          loading={isLoading}
          searchPlaceholder="Buscar módulo..."
          columnFilters={[
            { columnId: 'type', label: 'Tipo', options: Object.entries(TYPE_LABELS).map(([value, config]) => ({ value, label: config.label })) },
            { columnId: 'environments', label: 'Ambiente', options: environmentOptions },
          ]}
          toolbarButtons={[{
            label: 'Atualizar',
            icon: <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />,
            onClick: () => void refetch(),
            variant: 'subtle',
          }]}
          clientPagination
          emptyMessage="Nenhum módulo encontrado."
          stickyHeader
          maxHeight="min(55vh, 480px)"
        />
      </Dialog>
    </DashboardLayout>
  );
}
